/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import {
  deleteElasticsearchProject,
  deleteObservabilityProject,
  deleteSecurityProject,
  deleteWorkplaceAIProject,
} from '@modules/project-admin-api/v1/callers'
import type { ProjectId } from '@modules/project-admin-api/v1/types'
import type { QueryFunctionReturnType } from '@modules/query/types'
import type { ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'
import { queryClient } from '@modules/query'

import { makeQueryKey, linkCandidatesQueryKeys } from './helpers'

/**
 * Returns the appropriate hook for the provided project type.
 */
export function getDeleteProjectMutationHook(
  projectType: ProjectTypeWithWorkplaceAI,
):
  | typeof useDeleteElasticsearchProjectMutation
  | typeof useDeleteObservabilityProjectMutation
  | typeof useDeleteSecurityProjectMutation
  | typeof useDeleteWorkplaceAIProjectMutation {
  switch (projectType) {
    case 'observability':
      return useDeleteObservabilityProjectMutation
    case 'security':
      return useDeleteSecurityProjectMutation
    case 'workplaceai':
      return useDeleteWorkplaceAIProjectMutation
    default:
      return useDeleteElasticsearchProjectMutation
  }
}

const useDeleteElasticsearchProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof deleteElasticsearchProject>,
    unknown,
    { id: ProjectId }
  >({
    mutationFn: ({ id }) =>
      deleteElasticsearchProject({
        pathParameters: { id },
      }),
    onSuccess: (_, { id }) => onProjectDeleted('elasticsearch', id),
  })

const useDeleteObservabilityProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof deleteObservabilityProject>,
    unknown,
    { id: ProjectId }
  >({
    mutationFn: ({ id }) =>
      deleteObservabilityProject({
        pathParameters: { id },
      }),
    onSuccess: (_, { id }) => onProjectDeleted('observability', id),
  })

const useDeleteSecurityProjectMutation = () =>
  useMutation<QueryFunctionReturnType<typeof deleteSecurityProject>, unknown, { id: ProjectId }>({
    mutationFn: ({ id }) =>
      deleteSecurityProject({
        pathParameters: { id },
      }),
    onSuccess: (_, { id }) => onProjectDeleted('security', id),
  })

const useDeleteWorkplaceAIProjectMutation = () =>
  useMutation<QueryFunctionReturnType<typeof deleteWorkplaceAIProject>, unknown, { id: ProjectId }>(
    {
      mutationFn: ({ id }) =>
        deleteWorkplaceAIProject({
          pathParameters: { id },
        }),
      onSuccess: (_, { id }) => onProjectDeleted('workplaceai', id),
    },
  )

const onProjectDeleted = (type: ProjectTypeWithWorkplaceAI, id: ProjectId) => {
  const listQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'adminListElasticsearchProjects',
        observability: 'adminListObservabilityProjects',
        security: 'adminListSecurityProjects',
        workplaceai: 'adminListWorkplaceAIProjects',
      } as const
    )[type],
  )
  queryClient.invalidateQueries(listQueryKey)

  const detailQueryKey = makeQueryKey(
    (
      {
        elasticsearch: 'adminGetElasticsearchProject',
        observability: 'adminGetObservabilityProject',
        security: 'adminGetSecurityProject',
        workplaceai: 'adminGetWorkplaceAIProject',
      } as const
    )[type],
    id,
  )
  queryClient.invalidateQueries(detailQueryKey)

  queryClient.invalidateQueries({
    predicate: (query) => linkCandidatesQueryKeys.some((key) => query.queryKey[0] === key),
  })
}
